import { lazy, Suspense, useCallback, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { isTrialExpiredError } from "@/lib/trialError";
import { AuthGuard } from "@/components/AuthGuard";
import { SplashScreen } from "@/components/SplashScreen";
import { AppShell } from "@/components/AppShell";
import "@/i18n/config";

const LoginPage = lazy(() => import("@/routes/auth/login").then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() =>
  import("@/routes/auth/signup").then((m) => ({ default: m.SignupPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/routes/auth/forgot-password").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("@/routes/auth/reset-password").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const OnboardingPage = lazy(() =>
  import("@/routes/onboarding/index").then((m) => ({
    default: m.OnboardingPage,
  })),
);
const InventoryPage = lazy(() =>
  import("@/routes/inventory/index").then((m) => ({
    default: m.InventoryPage,
  })),
);
const OrdersPage = lazy(() =>
  import("@/routes/orders/index").then((m) => ({
    default: m.OrdersPage,
  })),
);
const MenuPage = lazy(() =>
  import("@/routes/menu/index").then((m) => ({
    default: m.MenuPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/routes/settings/index").then((m) => ({
    default: m.SettingsPage,
  })),
);
const LocationsPage = lazy(() =>
  import("@/routes/settings/locations").then((m) => ({
    default: m.LocationsPage,
  })),
);
const SuppliersPage = lazy(() =>
  import("@/routes/settings/suppliers").then((m) => ({
    default: m.SuppliersPage,
  })),
);
const PricingPage = lazy(() =>
  import("@/routes/pricing").then((m) => ({
    default: m.PricingPage,
  })),
);
const BillingSuccessPage = lazy(() =>
  import("@/routes/billing/success").then((m) => ({
    default: m.BillingSuccessPage,
  })),
);
const AccountPage = lazy(() =>
  import("@/routes/account/index").then((m) => ({
    default: m.AccountPage,
  })),
);
const AcceptInvitationPage = lazy(() =>
  import("@/routes/invitation/accept").then((m) => ({
    default: m.AcceptInvitationPage,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
  // Global safety net: any mutation the DB rejects because the trial expired
  // (SQLSTATE PT402) gets one clear message + a route to pricing, instead of a
  // raw error. Covers every write path, including ones the UI does not grey out
  // and API calls that bypass the UI entirely. Deduped by id so a bulk failure
  // shows a single toast.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isTrialExpiredError(error)) {
        toast.error("Okres próbny minął. Twoje dane są bezpieczne, ale edycja jest wstrzymana.", {
          id: "trial-expired",
          description: "Wybierz plan, aby wznowić edycję.",
          action: {
            label: "Wybierz plan",
            onClick: () => {
              window.location.href = "/cennik";
            },
          },
        });
      }
    },
  }),
});

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-pulse rounded-full bg-navy" />
    </div>
  );
}

export function App() {
  const [splashDone, setSplashDone] = useState(false);
  const handleDismiss = useCallback(() => setSplashDone(true), []);

  return (
    <>
      {!splashDone && <SplashScreen isReady={true} onDismiss={handleDismiss} />}
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthGuard>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/auth/signup" element={<SignupPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/billing/success" element={<BillingSuccessPage />} />
                <Route path="/zaproszenie/:token" element={<AcceptInvitationPage />} />
                <Route element={<AppShell />}>
                  <Route index element={<InventoryPage />} />
                  <Route path="/zamowienia" element={<OrdersPage />} />
                  <Route path="/menu" element={<MenuPage />} />
                  <Route path="/cennik" element={<PricingPage />} />
                  <Route path="/ustawienia" element={<SettingsPage />} />
                  <Route path="/ustawienia/lokalizacje" element={<LocationsPage />} />
                  <Route path="/ustawienia/dostawcy" element={<SuppliersPage />} />
                  <Route path="/konto" element={<AccountPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AuthGuard>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </QueryClientProvider>
    </>
  );
}
